import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import type { PrepRecipe, ProductionBatch, ProductionShortfall } from '@/types/kitchenFlowPrep'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { fmtQty } from '@/lib/quantity'
import { formatDateTimeHuman } from '@/lib/utils'
import { ingredientsForBatch, newIdempotencyKey, plainError, yieldInItemUnit, yieldVarianceText } from './prepRecipeDraft'
import { PREP_TEXT } from './prepStrings'
import { businessTodayIso, defaultExpiryFor, expiryMissing } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'

const BATCHES_QK = 'production-batches'

const STATUS_TEXT: Record<ProductionBatch['status'], string> = {
  planned: PREP_TEXT.statusPlanned,
  in_progress: PREP_TEXT.statusInProgress,
  done: PREP_TEXT.statusDone,
  cancelled: PREP_TEXT.statusCancelled,
}

/**
 * Make a batch of a prep item from the item drawer: say how much, see what it
 * takes from the kitchen, start it, then say how much it actually made.
 * Stock moves only on "Finished" (production_batches.go).
 */
export function PrepProduction({ recipe, showToast }: {
  recipe: PrepRecipe
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const itemId = recipe.stock_item_id
  const [planned, setPlanned] = useState(() => String(yieldInItemUnit(recipe) ?? ''))
  const [error, setError] = useState<string | null>(null)
  const [shortfalls, setShortfalls] = useState<ProductionShortfall[]>([])
  // One key per "Start making" attempt, kept across a retry of the same tap.
  const keyRef = useRef<string | null>(null)

  const { data: open = [] } = useQuery({
    queryKey: [BATCHES_QK, itemId, 'open'],
    queryFn: () => apiClient.listProductionBatches({ prep_item_id: itemId, status: ['planned', 'in_progress'] }).then((r) => r.data ?? []),
  })
  const { data: recent = [] } = useQuery({
    queryKey: [BATCHES_QK, itemId, 'recent'],
    queryFn: () => apiClient.listProductionBatches({ prep_item_id: itemId, status: ['done'], limit: 5 }).then((r) => r.data ?? []),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: [BATCHES_QK] })
    // Stock moved: the drawer's quantity, history and the item list.
    qc.invalidateQueries({ queryKey: ['stockItem'] })
    qc.invalidateQueries({ queryKey: ['stockItems'] })
    qc.invalidateQueries({ queryKey: ['stockMovements'] })
    qc.invalidateQueries({ queryKey: ['prep-recipe'] })
  }

  const plannedN = Number(planned)
  const needs = ingredientsForBatch(recipe, plannedN)

  const start = useMutation({
    mutationFn: async () => {
      keyRef.current ??= newIdempotencyKey()
      const created = await apiClient.createProductionBatch({ prep_item_id: itemId, planned_qty: plannedN }, keyRef.current)
      const b = created.data as ProductionBatch
      if (b.status === 'planned') await apiClient.startProductionBatch(b.id)
      return b
    },
    onSuccess: (b) => {
      keyRef.current = null
      setError(null)
      setShortfalls([])
      showToast('success', PREP_TEXT.batchStarted(b.batch_no))
      refresh()
    },
    onError: (e) => setError(plainError(e)),
  })

  return (
    <div className="space-y-4 border-t pt-4">
      <div>
        <h5 className="text-sm font-semibold">{PREP_TEXT.productionTitle}</h5>
        <div className="mt-2 flex items-center gap-2">
          <Input
            type="number" inputMode="decimal" min={0} step="any"
            className="w-32 tabular-nums"
            value={planned}
            onChange={(e) => setPlanned(e.target.value)}
            aria-label={PREP_TEXT.howMuchToMake}
          />
          <span className="text-sm">{recipe.unit}</span>
          <Button
            className="flex-1"
            disabled={!(plannedN > 0) || start.isPending}
            onClick={() => start.mutate()}
          >
            {start.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{PREP_TEXT.starting}</> : PREP_TEXT.startBatch}
          </Button>
        </div>
        {needs.length > 0 && (
          <div className="mt-2 rounded-md bg-muted/50 p-3 text-sm">
            <p className="mb-1 text-muted-foreground">{PREP_TEXT.willUse}</p>
            <ul className="space-y-0.5">
              {needs.map((n) => (
                <li key={n.stock_item_id} className="flex justify-between gap-3">
                  <span className="truncate">{n.name}</span>
                  <span className="shrink-0 tabular-nums font-medium">{fmtQty(n.qty)} {n.unit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {error && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

      {shortfalls.length > 0 && (
        <div role="alert" className="rounded-md bg-warning-surface/60 p-3 text-sm text-warning-fg">
          <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {PREP_TEXT.shortTitle}</p>
          <ul className="mt-1 space-y-0.5">
            {shortfalls.map((s) => (
              <li key={s.stock_item_id}>{PREP_TEXT.shortLine(s.name, `${fmtQty(s.needed)} ${s.unit}`, `${fmtQty(s.had)} ${s.unit}`)}</li>
            ))}
          </ul>
          <p className="mt-1">{PREP_TEXT.shortHelp}</p>
        </div>
      )}

      <div>
        <h5 className="mb-2 text-sm font-semibold">{PREP_TEXT.openBatches}</h5>
        {open.length === 0 ? (
          <p className="text-sm text-muted-foreground">{PREP_TEXT.noOpenBatches}</p>
        ) : (
          <ul className="space-y-2">
            {open.map((b) => (
              <OpenBatch
                key={b.id}
                batch={b}
                onDone={(res) => {
                  setShortfalls(res)
                  refresh()
                }}
                onCancelled={refresh}
                showToast={showToast}
              />
            ))}
          </ul>
        )}
      </div>

      {recent.length > 0 && (
        <div>
          <h5 className="mb-2 text-sm font-semibold">{PREP_TEXT.recentBatches}</h5>
          <ul className="divide-y rounded-md border text-sm">
            {recent.map((b) => (
              <li key={b.id} className="px-3 py-2">
                <div className="flex justify-between gap-3">
                  <span className="font-medium">{b.batch_no}</span>
                  <span className="tabular-nums">
                    {PREP_TEXT.made} {fmtQty(b.actual_yield_qty ?? 0)} / {PREP_TEXT.planned.toLowerCase()} {fmtQty(b.planned_qty)} {b.unit}
                  </span>
                </div>
                <div className="flex justify-between gap-3 text-xs text-muted-foreground">
                  <span>{b.completed_at ? formatDateTimeHuman(b.completed_at) : ''}{b.produced_by_name ? ` · ${b.produced_by_name}` : ''}</span>
                  <span>{yieldVarianceText(b, fmtQty)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function OpenBatch({ batch, onDone, onCancelled, showToast }: {
  batch: ProductionBatch
  onDone: (shortfalls: ProductionShortfall[]) => void
  onCancelled: () => void
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const [made, setMade] = useState(String(batch.planned_qty))
  // F-CTRL-N73: a batch of a tracked prep item needs an expiry date; its
  // shelf life fills one in until somebody types their own.
  const today = businessTodayIso(useDayBoundaryHour())
  const [typedExpiry, setExpiry] = useState<string | null>(null)
  const expiry = typedExpiry ?? defaultExpiryFor(batch, today)
  const [confirmZero, setConfirmZero] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const madeN = Number(made)

  const finish = useMutation({
    mutationFn: () => apiClient.completeProductionBatch(batch.id, { actual_yield_qty: madeN, expiry_date: expiry || undefined }),
    onSuccess: (res) => {
      showToast('success', PREP_TEXT.batchFinished(batch.batch_no))
      onDone(res.data?.shortfalls ?? [])
    },
    onError: (e) => setError(plainError(e)),
  })
  const cancel = useMutation({
    mutationFn: () => apiClient.cancelProductionBatch(batch.id),
    onSuccess: () => {
      showToast('success', PREP_TEXT.cancelled)
      onCancelled()
    },
    onError: (e) => setError(plainError(e)),
  })

  const onFinish = () => {
    if (madeN === 0 && !confirmZero) {
      setConfirmZero(true)
      return
    }
    finish.mutate()
  }

  return (
    <li className="rounded-md border p-3 space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium">{batch.batch_no}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{STATUS_TEXT[batch.status]}</span>
      </div>
      <p className="text-sm text-muted-foreground">
        {PREP_TEXT.planned}: {fmtQty(batch.planned_qty)} {batch.unit}
        {batch.created_by_name ? ` · ${batch.created_by_name}` : ''}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium">{PREP_TEXT.howMuchMade}</span>
          <div className="mt-1 flex items-center gap-2">
            <Input
              type="number" inputMode="decimal" min={0} step="any"
              className="tabular-nums"
              value={made}
              onChange={(e) => { setMade(e.target.value); setConfirmZero(false) }}
            />
            <span>{batch.unit}</span>
          </div>
        </label>
        <label className="block text-sm">
          <span className={batch.tracks_expiry && madeN > 0 && !expiry ? 'font-medium text-danger-fg' : 'font-medium'}>
            {batch.tracks_expiry ? `${PREP_TEXT.expiryRequiredLabel}*` : PREP_TEXT.expiryLabel}
          </span>
          <Input type="date" className="mt-1" aria-required={batch.tracks_expiry} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </label>
      </div>
      {confirmZero && <p className="text-sm text-warning-fg">{PREP_TEXT.zeroYieldConfirm}</p>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={made.trim() === '' || !(madeN >= 0) || (madeN > 0 && expiryMissing(batch, expiry)) || finish.isPending || cancel.isPending}
          onClick={onFinish}
        >
          {finish.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{PREP_TEXT.finishing}</> : PREP_TEXT.finish}
        </Button>
        <Button variant="outline" disabled={finish.isPending || cancel.isPending} onClick={() => cancel.mutate()}>
          {PREP_TEXT.cancelBatch}
        </Button>
      </div>
    </li>
  )
}
